import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  useQpmOfferChangeDocumentProfileMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import {
  ChangeOfferDocumentProfileRequest,
  Offer,
} from "@/services/backend/qpm/offers/types/offer";
import {
  ChangeMode,
  ConfigOrigin,
  ExtensionAreaScope,
  OfferType,
} from "@/services/backend/qpm/offers/types/offer-enum";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import {
  deriveTargetOrganismOptionsFromMonitorSnaps,
  TargetOrganismSelectionEditor,
  type TargetOrganismSelectionValue,
  deriveContractAgainstTextFromTargetOrganismSelection,
  isSameTargetOrganismSelection,
  normalizeTargetOrganismSelectionValue,
} from "@/shared/components/domain/qpm/target-organism-selection-editor";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  rtkErrIsValidationError,
  ValidationError,
} from "@/shared/lib/errors/validation-error";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import { InfoIconTooltip } from "@/shared/components/domain/qpm/info-icon-tooltip";

function toMode(origin: ConfigOrigin): ChangeMode {
  return origin === ConfigOrigin.Manual
    ? ChangeMode.Manual
    : ChangeMode.Automatic;
}

function toConfigPatch<T extends string | number>(value: T, mode: ChangeMode) {
  if (mode === ChangeMode.Automatic) {
    return { mode: ChangeMode.Automatic };
  }

  return { mode: ChangeMode.Manual, value };
}

function targetAreaScopeLabel(value: ExtensionAreaScope): string {
  switch (value) {
    case ExtensionAreaScope.Outdoor:
      return t("Außenbereich");
    case ExtensionAreaScope.IndoorOutdoor:
      return t("Innen- und Außenbereich");
    default:
      return t("Innenbereich");
  }
}

function buildRequest({
  offer,
  contractAgainstText,
  contractAgainstMode,
  targetAreaScope,
  targetAreaScopeMode,
  selection,
  savedSelection,
}: {
  offer: Offer;
  contractAgainstText: string;
  contractAgainstMode: ChangeMode;
  targetAreaScope: ExtensionAreaScope;
  targetAreaScopeMode: ChangeMode;
  selection: TargetOrganismSelectionValue;
  savedSelection: TargetOrganismSelectionValue;
}): ChangeOfferDocumentProfileRequest {
  const isExtensionOffer =
    offer.documentProfile.offerType.value === OfferType.Extension;

  const documentProfile: ChangeOfferDocumentProfileRequest["documentProfile"] =
    {};

  if (isExtensionOffer) {
    documentProfile.contractAgainstText = toConfigPatch(
      contractAgainstText,
      contractAgainstMode,
    );
    documentProfile.targetAreaScope = toConfigPatch(
      targetAreaScope,
      targetAreaScopeMode,
    );
  }

  const next: ChangeOfferDocumentProfileRequest = {
    offerId: offer.id,
    documentProfile,
  };

  if (!isSameTargetOrganismSelection(savedSelection, selection)) {
    next.ioTTargetOrganismSelection = {
      mode: ChangeMode.Manual,
      categoryKeys: [...selection.categoryKeys],
      typeKeys: [...selection.typeKeys],
    };
  }

  return next;
}

export function OfferIoTTargetOrganismEditor({
  offer,
  disabledByPriceVisibility = false,
}: {
  offer: Offer;
  disabledByPriceVisibility?: boolean;
}) {
  const { setValidationError } = useOfferContext();

  const [
    changeDocumentProfile,
    {
      error: changeDocumentProfileError,
      isLoading: isSavingDocumentProfile,
      isSuccess: isChangeDocumentProfileSuccess,
      reset: resetChangeDocumentProfile,
    },
  ] = useQpmOfferChangeDocumentProfileMutation();
  const [reloadSnapshot] = useQpmOfferReloadOfferConfigSnapshotMutation();

  const isExtensionOffer =
    offer.documentProfile.offerType.value === OfferType.Extension;
  const hasIoTMonitors = Object.values(offer.monitorSnaps ?? {}).some(
    (snap) => snap.type === MonitorType.IoT,
  );

  const { categoryOptions, typeOptions } = useMemo(
    () => deriveTargetOrganismOptionsFromMonitorSnaps(offer.monitorSnaps),
    [offer],
  );

  const initialSelection = useMemo<TargetOrganismSelectionValue>(
    () =>
      normalizeTargetOrganismSelectionValue({
        categoryKeys: offer.ioTTargetOrganismSelection?.categoryKeys ?? [],
        typeKeys: offer.ioTTargetOrganismSelection?.typeKeys ?? [],
      }),
    [
      offer.ioTTargetOrganismSelection?.categoryKeys,
      offer.ioTTargetOrganismSelection?.typeKeys,
    ],
  );

  const [selection, setSelection] =
    useState<TargetOrganismSelectionValue>(initialSelection);
  const [savedSelection, setSavedSelection] =
    useState<TargetOrganismSelectionValue>(initialSelection);

  const [contractAgainstText, setContractAgainstText] = useState<string>(
    offer.documentProfile.extension?.contractAgainstText?.value ?? "",
  );
  const [contractAgainstMode, setContractAgainstMode] = useState<ChangeMode>(
    toMode(
      offer.documentProfile.extension?.contractAgainstText?.origin ??
        ConfigOrigin.Derived,
    ),
  );

  const [targetAreaScope, setTargetAreaScope] = useState<ExtensionAreaScope>(
    offer.documentProfile.extension?.targetAreaScope?.value ??
      ExtensionAreaScope.Indoor,
  );
  const [targetAreaScopeMode, setTargetAreaScopeMode] = useState<ChangeMode>(
    toMode(
      offer.documentProfile.extension?.targetAreaScope?.origin ??
        ConfigOrigin.Derived,
    ),
  );

  const [savedRequest, setSavedRequest] =
    useState<ChangeOfferDocumentProfileRequest | null>(null);
  const initializedStateKey = useRef<string | null>(null);
  const lastSubmittedRequest = useRef<ChangeOfferDocumentProfileRequest | null>(
    null,
  );

  useEffect(() => {
    const nextStateKey = JSON.stringify({
      offerId: offer.id,
      selection: initialSelection,
      contractAgainstText:
        offer.documentProfile.extension?.contractAgainstText?.value ?? "",
      contractAgainstMode:
        offer.documentProfile.extension?.contractAgainstText?.origin ??
        ConfigOrigin.Derived,
      targetAreaScope:
        offer.documentProfile.extension?.targetAreaScope?.value ??
        ExtensionAreaScope.Indoor,
      targetAreaScopeMode:
        offer.documentProfile.extension?.targetAreaScope?.origin ??
        ConfigOrigin.Derived,
    });

    if (initializedStateKey.current === nextStateKey) {
      return;
    }

    initializedStateKey.current = nextStateKey;
    const initialContractAgainstText =
      offer.documentProfile.extension?.contractAgainstText?.value ?? "";
    const initialContractAgainstMode = toMode(
      offer.documentProfile.extension?.contractAgainstText?.origin ??
        ConfigOrigin.Derived,
    );
    const initialTargetAreaScope =
      offer.documentProfile.extension?.targetAreaScope?.value ??
      ExtensionAreaScope.Indoor;
    const initialTargetAreaScopeMode = toMode(
      offer.documentProfile.extension?.targetAreaScope?.origin ??
        ConfigOrigin.Derived,
    );

    setSelection(initialSelection);
    setSavedSelection(initialSelection);
    setContractAgainstText(initialContractAgainstText);
    setContractAgainstMode(initialContractAgainstMode);
    setTargetAreaScope(initialTargetAreaScope);
    setTargetAreaScopeMode(initialTargetAreaScopeMode);
    setSavedRequest(
      buildRequest({
        offer,
        contractAgainstText: initialContractAgainstText,
        contractAgainstMode: initialContractAgainstMode,
        targetAreaScope: initialTargetAreaScope,
        targetAreaScopeMode: initialTargetAreaScopeMode,
        selection: initialSelection,
        savedSelection: initialSelection,
      }),
    );
  }, [initialSelection, offer]);

  useEffect(() => {
    if (contractAgainstMode !== ChangeMode.Automatic) {
      return;
    }

    const derived =
      deriveContractAgainstTextFromTargetOrganismSelection(selection);
    setContractAgainstText((current) =>
      current === derived ? current : derived,
    );
  }, [contractAgainstMode, selection]);

  const requestDraft = useMemo<ChangeOfferDocumentProfileRequest>(
    () =>
      buildRequest({
        offer,
        contractAgainstText,
        contractAgainstMode,
        targetAreaScope,
        targetAreaScopeMode,
        selection,
        savedSelection,
      }),
    [
      offer,
      contractAgainstText,
      contractAgainstMode,
      targetAreaScope,
      targetAreaScopeMode,
      selection,
      savedSelection,
    ],
  );

  const hasPendingChanges = useMemo(() => {
    if (!savedRequest) {
      return false;
    }

    return JSON.stringify(savedRequest) !== JSON.stringify(requestDraft);
  }, [requestDraft, savedRequest]);

  const onDebouncedChangeDocumentProfile = useCallback(
    (nextRequest: ChangeOfferDocumentProfileRequest) => {
      lastSubmittedRequest.current = nextRequest;
      changeDocumentProfile(nextRequest);
    },
    [changeDocumentProfile],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    requestDraft,
    onDebouncedChangeDocumentProfile,
    isSavingDocumentProfile,
    changeDocumentProfileError,
    isChangeDocumentProfileSuccess,
    resetChangeDocumentProfile,
    500,
    {
      skip: offer.immutable || !hasPendingChanges,
      toastError: true,
    },
  );

  useEffect(() => {
    if (
      !changeDocumentProfileError ||
      !rtkErrIsValidationError(changeDocumentProfileError)
    ) {
      return;
    }

    setValidationError(changeDocumentProfileError.data as ValidationError);
  }, [changeDocumentProfileError, setValidationError]);

  useEffect(() => {
    if (!isChangeDocumentProfileSuccess || !lastSubmittedRequest.current) {
      return;
    }

    setSavedRequest(lastSubmittedRequest.current);

    const submittedSelection =
      lastSubmittedRequest.current.ioTTargetOrganismSelection;
    if (submittedSelection) {
      setSavedSelection(
        normalizeTargetOrganismSelectionValue({
          categoryKeys: submittedSelection.categoryKeys ?? [],
          typeKeys: submittedSelection.typeKeys ?? [],
        }),
      );
    }

    reloadSnapshot({ offerId: offer.id });
    setValidationError(null);
  }, [
    isChangeDocumentProfileSuccess,
    offer.id,
    reloadSnapshot,
    setValidationError,
  ]);

  const selectionDisabled =
    offer.immutable || !hasIoTMonitors || disabledByPriceVisibility;
  const settingsDisabled =
    offer.immutable || !isExtensionOffer || disabledByPriceVisibility;

  return (
    <div
      className={`space-y-4 ${disabledByPriceVisibility ? "opacity-50" : ""}`}
    >
      <div className="grid content-start gap-1.5">
        <div className="flex items-center gap-2">
          <Label className="min-h-5 text-sm">
            {t("Schädlingsauswahl (IoT)")}
          </Label>
          {disabledByPriceVisibility ? (
            <InfoIconTooltip
              text={t(
                "Diese Inhalte sind im PDF ausgeblendet, da die zugehörige Preisposition in den Preispositionen deaktiviert wurde.",
              )}
            />
          ) : null}
        </div>
        <TargetOrganismSelectionEditor
          selection={selection}
          onChange={(next) => {
            const normalized = normalizeTargetOrganismSelectionValue(next);
            setSelection((current) => {
              if (isSameTargetOrganismSelection(current, normalized)) {
                return current;
              }
              return normalized;
            });
          }}
          disabled={selectionDisabled}
          categoryOptions={categoryOptions}
          typeOptions={typeOptions}
        />
      </div>

      <div className="grid content-start gap-1.5">
        <Label className="min-h-5 text-sm">{t("gegen")}</Label>
        <div className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)]">
          <Select
            value={contractAgainstMode}
            disabled={settingsDisabled}
            onValueChange={(next) => {
              setContractAgainstMode((current) => {
                const value = next as ChangeMode;
                if (current === value) {
                  return current;
                }
                return value;
              });
            }}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ChangeMode.Manual}>{t("Manuell")}</SelectItem>
              <SelectItem value={ChangeMode.Automatic}>
                {t("Automatisch")}
              </SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            rows={3}
            value={contractAgainstText}
            disabled={
              settingsDisabled || contractAgainstMode === ChangeMode.Automatic
            }
            onChange={(event) => {
              const next = event.target.value;
              setContractAgainstText((current) =>
                current === next ? current : next,
              );
            }}
          />
        </div>
        <p className="text-muted-foreground text-sm">
          {t("Der Bereich wird im PDF automatisch an den Text angehängt.")}
        </p>
      </div>

      <div className="grid content-start gap-1.5">
        <Label className="min-h-5 text-sm">{t("Bereich")}</Label>
        <div className="grid gap-2 md:grid-cols-[160px_minmax(0,1fr)]">
          <Select
            value={targetAreaScopeMode}
            disabled={settingsDisabled}
            onValueChange={(next) => {
              setTargetAreaScopeMode((current) => {
                const value = next as ChangeMode;
                if (current === value) {
                  return current;
                }
                return value;
              });
            }}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ChangeMode.Manual}>{t("Manuell")}</SelectItem>
              <SelectItem value={ChangeMode.Automatic}>
                {t("Automatisch")}
              </SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={targetAreaScope}
            disabled={
              settingsDisabled || targetAreaScopeMode === ChangeMode.Automatic
            }
            onValueChange={(next) => {
              setTargetAreaScope((current) => {
                const value = next as ExtensionAreaScope;
                if (current === value) {
                  return current;
                }
                return value;
              });
            }}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ExtensionAreaScope.Indoor}>
                {targetAreaScopeLabel(ExtensionAreaScope.Indoor)}
              </SelectItem>
              <SelectItem value={ExtensionAreaScope.Outdoor}>
                {targetAreaScopeLabel(ExtensionAreaScope.Outdoor)}
              </SelectItem>
              <SelectItem value={ExtensionAreaScope.IndoorOutdoor}>
                {targetAreaScopeLabel(ExtensionAreaScope.IndoorOutdoor)}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <RTKQueryErrorAlert error={changeDocumentProfileError} />
    </div>
  );
}
